import { db } from '@/db/client';
import { getPlayerGroup } from '@/db/player-groups';
import { getPlayer } from '@/db/players';
import { getTeamPreset } from '@/db/team-presets';
import type { ImageId, PlayerGroupId, PlayerId, SessionId, TeamId, UserId } from '@/schemas/ids';
import { parsePlayerGroupId, parseTeamId } from '@/schemas/ids';
import type { ImageEntityType, ImageRecord, ImageUploadStatus } from '@/schemas/image';
import type { Player } from '@/schemas/player';
import type { PlayerGroup } from '@/schemas/player-group';
import type { TeamPreset } from '@/schemas/team';

/**
 * Get all images for a specific entity
 * @param entityType - Type of entity (player, session, game, team, round)
 * @param entityId - ID of the entity
 * @returns Array of image records
 */
export async function getImagesByEntity(
  entityType: ImageEntityType,
  entityId: string,
): Promise<ImageRecord[]> {
  return db.images
    .where('[entityType+entityId]')
    .equals([entityType, entityId])
    .filter((img) => !img.deletedAt)
    .toArray();
}

/**
 * Get all images for a session (for privacy filtering)
 * @param sessionId - Session ID
 * @returns Array of image records
 */
export async function getImagesBySession(sessionId: SessionId): Promise<ImageRecord[]> {
  return db.images
    .where('sessionId')
    .equals(sessionId)
    .filter((img) => !img.deletedAt)
    .sortBy('uploadedAt');
}

/**
 * Get images by upload status
 * @param status - Upload status to filter by
 * @returns Array of image records
 */
export async function getImagesByStatus(status: ImageUploadStatus): Promise<ImageRecord[]> {
  return db.images
    .where('uploadStatus')
    .equals(status)
    .filter((img) => !img.deletedAt)
    .toArray();
}

/**
 * Get all pending uploads (for sync)
 * @returns Array of image records with pending status
 */
export async function getPendingUploads(): Promise<ImageRecord[]> {
  return getImagesByStatus('pending');
}

/**
 * Get all failed uploads (for retry)
 * @returns Array of image records with failed status
 */
export async function getFailedUploads(): Promise<ImageRecord[]> {
  return getImagesByStatus('failed');
}

/**
 * Get all images uploaded by a specific user
 * @param userId - User ID
 * @returns Array of image records
 */
export async function getImagesByUser(userId: UserId): Promise<ImageRecord[]> {
  // Note: userId is stored in the image record but not indexed
  // This query scans all images, so use sparingly
  return db.images.filter((img) => img.userId === userId && !img.deletedAt).toArray();
}

/**
 * Get a single image by ID
 * @param imageId - Image ID
 * @returns Image record or undefined
 */
export async function getImageById(imageId: ImageId): Promise<ImageRecord | undefined> {
  return db.images.get(imageId);
}

/**
 * Get images uploaded after a specific timestamp
 * @param uploadedAfter - ISO timestamp string
 * @returns Array of image records
 */
export async function getImagesUploadedAfter(uploadedAfter: string): Promise<ImageRecord[]> {
  const timestamp = new Date(uploadedAfter).getTime();
  return db.images
    .filter((img) => {
      const imgTimestamp = new Date(img.uploadedAt).getTime();
      return imgTimestamp > timestamp && !img.deletedAt;
    })
    .toArray();
}

/**
 * Get all players linked to an image
 * Players are linked via:
 * 1. avatarImageId (player uses image as avatar)
 * 2. entityType='player' and entityId matches player.id (image is directly linked to player)
 * @param imageId - Image ID
 * @returns Array of player records (filtered to exclude deleted players)
 */
export async function getPlayersByImageId(imageId: ImageId): Promise<Player[]> {
  const image = await getImageById(imageId);
  if (!image) {
    return [];
  }

  const players: Player[] = [];

  // Get players where this image is their avatar
  const playersWithAvatar = await db.players
    .filter((player) => player.avatarImageId === imageId && !player.deletedAt)
    .toArray();

  for (const player of playersWithAvatar) {
    const parsedPlayer = await getPlayer(player.id);
    if (parsedPlayer) {
      players.push(parsedPlayer);
    }
  }

  // Get player if image is directly linked to a player (entityType='player')
  if (image.entityType === 'player') {
    try {
      const directPlayer = await getPlayer(image.entityId as PlayerId);
      const isValidPlayer =
        directPlayer && !directPlayer.deletedAt && !players.some((p) => p.id === directPlayer.id);
      if (isValidPlayer) {
        players.push(directPlayer);
      }
    } catch {
      // Invalid player ID, skip
    }
  }

  return players;
}

/**
 * Get all teams linked to an image
 * Teams are linked via:
 * 1. avatarImageId (team uses image as avatar)
 * 2. entityType='team' and entityId matches team.id (image is directly linked to team)
 * @param imageId - Image ID
 * @returns Array of team preset records (filtered to exclude deleted teams)
 */
export async function getTeamsByImageId(imageId: ImageId): Promise<TeamPreset[]> {
  const image = await getImageById(imageId);
  if (!image) {
    return [];
  }

  const teams: TeamPreset[] = [];

  // Get teams where this image is their avatar
  const teamsWithAvatar = await db.teamPresets
    .filter((team) => team.avatarImageId === imageId && !team.deletedAt)
    .toArray();

  for (const team of teamsWithAvatar) {
    const parsedTeam = await getTeamPreset(team.id);
    if (parsedTeam) {
      teams.push(parsedTeam);
    }
  }

  // Get team if image is directly linked to a team (entityType='team')
  if (image.entityType === 'team') {
    try {
      const directTeam = await getTeamPreset(parseTeamId(image.entityId as TeamId));
      const isValidTeam =
        directTeam && !directTeam.deletedAt && !teams.some((t) => t.id === directTeam.id);
      if (isValidTeam) {
        teams.push(directTeam);
      }
    } catch {
      // Invalid team ID, skip
    }
  }

  return teams;
}

/**
 * Get all player groups linked to an image
 * Groups are linked via:
 * 1. avatarImageId (group uses image as avatar)
 * 2. entityType='group' and entityId matches group.id (image is directly linked to group)
 * @param imageId - Image ID
 * @returns Array of player group records (filtered to exclude deleted groups)
 */
export async function getGroupsByImageId(imageId: ImageId): Promise<PlayerGroup[]> {
  const image = await getImageById(imageId);
  if (!image) {
    return [];
  }

  const groups: PlayerGroup[] = [];

  // Get groups where this image is their avatar
  const groupsWithAvatar = await db.playerGroups
    .filter((group) => group.avatarImageId === imageId && !group.deletedAt)
    .toArray();

  for (const group of groupsWithAvatar) {
    const parsedGroup = await getPlayerGroup(group.id);
    if (parsedGroup) {
      groups.push(parsedGroup);
    }
  }

  // Get group if image is directly linked to a group (entityType='group')
  if (image.entityType === 'group') {
    try {
      const directGroup = await getPlayerGroup(parsePlayerGroupId(image.entityId as PlayerGroupId));
      const isValidGroup =
        directGroup && !directGroup.deletedAt && !groups.some((g) => g.id === directGroup.id);
      if (isValidGroup) {
        groups.push(directGroup);
      }
    } catch {
      // Invalid group ID, skip
    }
  }

  return groups;
}
